import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { X, FileText, Shield, Users } from 'lucide-react';
import { API } from '@/App';

const LegalConsentModal = ({ isOpen, onClose, onAccept }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('terms');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [antiSharingAccepted, setAntiSharingAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState({
    terms: '',
    privacy: '',
    antiSharing: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchDocuments();
    }
  }, [isOpen]);

  const fetchDocuments = async () => {
    try {
      const [termsRes, privacyRes, antiSharingRes] = await Promise.all([
        axios.get(`${API}/legal/terms`),
        axios.get(`${API}/legal/privacy`),
        axios.get(`${API}/legal/anti-sharing`)
      ]);

      setDocuments({
        terms: termsRes.data.content,
        privacy: privacyRes.data.content,
        antiSharing: antiSharingRes.data.content
      });
    } catch (error) {
      console.error('Error fetching legal documents:', error);
    }
  };

  const handleAcceptAll = async () => {
    if (!termsAccepted || !privacyAccepted || !antiSharingAccepted) {
      alert(t('legal.readAndAccept'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/legal/accept`, {
        terms: true,
        privacy: true,
        anti_sharing: true
      });
      onAccept();
      onClose();
    } catch (error) {
      console.error('Error accepting legal terms:', error);
      alert(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'terms', label: t('legal.termsOfUse'), icon: FileText },
    { id: 'privacy', label: t('legal.privacyPolicy'), icon: Shield },
    { id: 'antiSharing', label: t('legal.antiSharing'), icon: Users }
  ];

  const renderContent = () => {
    const content = documents[activeTab];
    return (
      <div className="prose prose-invert max-w-none">
        <pre className="whitespace-pre-wrap text-sm text-gray-300 font-sans">
          {content}
        </pre>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-[#1a1a1a] rounded-xl border border-[#a3e635]/20 max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <div>
            <h2 className="text-2xl font-bold text-white">{t('legal.title')}</h2>
            <p className="text-gray-400 text-sm mt-1">{t('legal.readAndAccept')}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-800 px-6">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#a3e635] text-[#a3e635]'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderContent()}
        </div>

        {/* Footer with checkboxes */}
        <div className="border-t border-gray-800 p-6 space-y-4">
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer group select-none">
              <div className="relative flex-shrink-0">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="peer sr-only"
                />
                <div className="w-6 h-6 rounded border-2 border-gray-600 bg-black/50 peer-checked:bg-[#a3e635] peer-checked:border-[#a3e635] transition-all flex items-center justify-center">
                  {termsAccepted && (
                    <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                {t('legal.iAcceptThe')} <strong>{t('legal.termsOfUse')}</strong>
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer group select-none">
              <div className="relative flex-shrink-0">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="peer sr-only"
                />
                <div className="w-6 h-6 rounded border-2 border-gray-600 bg-black/50 peer-checked:bg-[#a3e635] peer-checked:border-[#a3e635] transition-all flex items-center justify-center">
                  {privacyAccepted && (
                    <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                {t('legal.iAcceptThe')} <strong>{t('legal.privacyPolicy')}</strong> (GDPR)
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer group select-none">
              <div className="relative flex-shrink-0">
                <input
                  type="checkbox"
                  checked={antiSharingAccepted}
                  onChange={(e) => setAntiSharingAccepted(e.target.checked)}
                  className="peer sr-only"
                />
                <div className="w-6 h-6 rounded border-2 border-gray-600 bg-black/50 peer-checked:bg-[#a3e635] peer-checked:border-[#a3e635] transition-all flex items-center justify-center">
                  {antiSharingAccepted && (
                    <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                {t('legal.iAcceptThe')} <strong>{t('legal.antiSharing')}</strong>
              </span>
            </label>
          </div>

          <button
            onClick={handleAcceptAll}
            disabled={!termsAccepted || !privacyAccepted || !antiSharingAccepted || loading}
            className={
              `w-full py-3 rounded-lg font-semibold transition-all ${
                termsAccepted && privacyAccepted && antiSharingAccepted && !loading
                  ? 'bg-[#a3e635] text-black hover:bg-[#8bc422] active:scale-95'
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`
            }
          >
            {loading ? t('common.loading') : t('legal.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalConsentModal;